import ScrollerButton from '@/components/ScrollerButton';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useEffect, useRef, useState } from 'react';
import { type HeroBannerProps } from './HeroBanner.types';

const HeroBanner: React.FC<HeroBannerProps> = ({
  title,
  titleColor,
  summary,
  summaryColor,
  desktopImage,
  desktopFocusPoint,
  mobileImage,
  mobileFocusPoint,
  mobileAspectRatio,
  scrollTo,
}) => {
  const heroBannerRef = useRef<HTMLDivElement>(null);
  const { isDesktop } = useBreakpoint();
  const desktopStyle = desktopImage
    ? {
        backgroundImage: `url(${desktopImage})`,
        backgroundPosition: desktopFocusPoint,
      }
    : {};

  const mobileStyles = mobileImage
    ? {
        backgroundImage: `url(${mobileImage})`,
        backgroundPosition: mobileFocusPoint,
        aspectRatio: mobileAspectRatio,
      }
    : {};
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // replace any <br> in title with a newline
  if (!title) {
    title = '';
  }

  title = title.replace(/<br>/g, '\n');

  const headingStyle: React.CSSProperties = {};
  const summaryStyle: React.CSSProperties = {};

  useEffect(() => {
    if (!heroBannerRef) return;

    // Return is mobile view when root view is smaller than 768px
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setIsMobile(width < 768);
    });

    observer.observe(document.documentElement);

    return () => {
      observer.disconnect();
    };
  });

  if (isDesktop) {
    if (titleColor) {
      headingStyle.color = titleColor;
    }

    if (summaryColor) {
      summaryStyle.color = summaryColor;
    }
  }

  if (isMobile) {
    if (!summary) {
      headingStyle.marginBottom = '100px';
    }
  }

  return (
    <div className="hero-page-header" ref={heroBannerRef}>
      {desktopImage && !isMobile && (
        <div className="hero-page-header__image" style={desktopStyle} />
      )}

      {mobileImage && isMobile && (
        <div
          className="hero-page-header__image hero-page-header__image--mobile"
          style={mobileStyles}
        />
      )}

      <div className="hero-page-header__container container">
        <div className="hero-page-header__main" data-aos="fade-in">
          <h1 className="hero-page-header__heading" style={headingStyle}>
            {title}
          </h1>

          {summary && (
            <p
              className="hero-page-header__summary h3 h3--light"
              style={summaryStyle}
            >
              {summary}
            </p>
          )}
        </div>

        {scrollTo && (
          <ScrollerButton
            extraClasses="hero-page-header__button"
            scrollToElement={scrollTo}
          />
        )}
      </div>
    </div>
  );
};

export default HeroBanner;
